import { toast } from 'react-toastify';
import { Button } from '@accure/ui';
import { AlertTriangle, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/dialog';

import type { Role } from '../entities/role';
import { DeleteRoleMutation } from '../mutations/delete-role';

export type DeleteRoleConfirmationDialogProps = {
  role: Role | undefined;
  onClose: () => void;
};

export function DeleteRoleConfirmationDialog({
  role,
  onClose,
}: DeleteRoleConfirmationDialogProps) {
  const deleteRoleMutation = DeleteRoleMutation.useMutation();

  async function handleDelete() {
    if (!role) {
      return;
    }

    await toast.promise(deleteRoleMutation.mutateAsync({ id: role.id }), {
      pending: 'Deleting role...',
      success: 'Role deleted successfully!',
      error: 'Failed to delete role. Please try again.',
    });

    onClose();
  }

  return (
    <Dialog open={Boolean(role)} onClose={onClose}>
      {role ? (
        <>
          <DialogTitle>
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="size-5 text-red-600" />
              </div>
              <span>Delete the role {role.name}?</span>
            </div>
          </DialogTitle>
          <DialogBody>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete the role? This action cannot be
              undone.
            </p>
          </DialogBody>
          <DialogActions>
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              type="button"
              onClick={handleDelete}
              disabled={deleteRoleMutation.isLoading}
            >
              {deleteRoleMutation.isLoading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Deleting
                </>
              ) : (
                'Delete Role'
              )}
            </Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}
